import { TFunction } from 'i18next';
import { SurveySteps } from './types/enums';
import { PaymentMethod } from '@pec/aion-ui-core/enums/Subscription';

export const schema = (t: TFunction, recurringBillingEnabled: boolean, bankTransferEnabled: boolean) => ({
  requiredText: '',
  pages: [
    {
      title: t('signup:pages.yourDetails', 'Your details'),
      stepName: SurveySteps.YourDetails,
      elements: [
        {
          type: 'html',
          name: 'explanation',
          html: `<p class="explanation sd-element"> {marketingText} </p>`
        },
        {
          type: 'mui-text',
          name: 'firstName',
          title: t('signup:fields.firstName', 'First name'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.firstName', 'First name'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 35,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.firstName', 'First name'),
                number: 35
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.firstName', 'First name'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: "^[A-Za-z- ']+$",
              text: t('signup:validation.charSet', {
                defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                field: t('signup:fields.firstName', 'First name'),
                charSet: "a-z A-Z - '"
              })
            }
          ]
        },
        {
          type: 'mui-text',
          name: 'lastName',
          title: t('signup:fields.lastName', 'Last name'),
          startWithNewLine: false,
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.lastName', 'Last name'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 35,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.lastName', 'Last name'),
                number: 35
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.lastName', 'Last name'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: "^[A-Za-z- ']+$",
              text: t('signup:validation.charSet', {
                defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                field: t('signup:fields.lastName', 'Last name'),
                charSet: "a-z A-Z - '"
              })
            }
          ]
        },
        {
          type: 'mui-text',
          name: 'email',
          title: t('signup:fields.emailAddress', 'Email address'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.emailAddress', 'Email address'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 80,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.emailAddress', 'Email address'),
                number: 80
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.emailAddress', 'Email address'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: '^(?=.{6,256}$)([+\\w\\.\\-]+)@(([\\w\\-]+\\.)+[\\w\\-]{2,})$',
              text: t(
                'signup:validation.includeValidEmail',
                'Please include valid e-mail box value, example: example@example.com'
              )
            },
            {
              type: 'expression',
              text: t('signup:validation.emailAlreadyExists', 'Email already exists.'),
              expression: 'checkIfEmailExists({email})'
            }
          ]
        },
        {
          type: 'mui-phone-input',
          name: 'phoneNumber',
          title: t('signup:fields.contactPhoneNumber', 'Contact phone number'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.requirePhoneNumber', 'Phone number is required'),
          validators: [
            {
              type: 'expression',
              expression: 'validatePhoneNumber({phoneNumber})',
              text: t('signup:validation.enterValidPhoneNumber', 'Enter a valid phone number')
            }
          ]
        }
      ],
      stepNumber: 1
    },
    {
      title: t('signup:pages.membershipPackage', 'Membership package'),
      stepName: SurveySteps.MembershipPackage,
      elements: [
        {
          type: 'mui-radiogroup',
          name: 'packageType',
          title: t('signup:fields.packageType', 'Where do you work?'),
          description: t(
            'signup:fields.packageTypeDescription',
            'Select the location where your company does business.'
          ),
          isRequired: true,
          visibleIf: 'showPackageType()',
          clearIfInvisible: 'none',
          choices: [
            { value: 'GB', text: t('signup:packageType.unitedKingdom', 'United Kingdom') },
            { value: 'AU', text: t('signup:packageType.australia', 'Australia') }
          ],
          requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.')
        },
        {
          type: 'mui-radiogroup',
          name: 'organizationType',
          title: t('signup:fields.organizationType', 'Are you a contractor or a supplier?'),
          isRequired: true,
          choices: [
            {
              value: 'Contractor',
              text: t('signup:organizationType.contractor', 'Contractor'),
              tooltip: t(
                'signup:organizationType.contractorExplanationText',
                'Contractors are construction workers or skilled tradespeople who work predominantly onsite and are responsible for installation, maintenance or repairs.'
              )
            },
            {
              value: 'GroupContractor',
              text: t('signup:organizationType.groupContractor', 'Group contractor'),
              visibleIf: '{isAdmin} == true && {packageType} = GB'
            },
            {
              value: 'Supplier',
              text: t('signup:organizationType.supplier', 'Supplier'),
              tooltip: t(
                'signup:organizationType.supplierExplanationText',
                'Suppliers provide off-site goods or services for use in projects, typically construction materials, industrial supplies or other raw materials.'
              )
            }
          ],
          requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.')
        }
      ],
      stepNumber: 2
    },
    {
      title: t('signup:pages.applicationType', 'Application type'),
      stepName: SurveySteps.ApplicationType,
      elements: [
        {
          type: 'mui-list',
          name: 'applicationType',
          title: t('signup:fields.chooseApplicationType', 'Choose your Health & Safety application type'),
          isRequired: true,
          listAlert: {
            variant: 'info',
            label: t(
              'signup:applicationType.informationDialog',
              'Please ensure you select the correct health and safety application type as selecting the wrong type could delay your assessment.'
            )
          },
          choices: [
            {
              value: 'Contractor - Construction',
              text: t('signup:applicationType.construction', 'Construction'),
              iconSrc: '/static/images/Construction.svg',
              tooltip: t(
                'signup:applicationType.constructionDetails',
                'Any work carried out in connection with the construction, alteration, conversion,  fitting-out, commissioning, renovation, repair, maintenance, refurbishment, demolition, decommissioning or dismantling of a structure, or preparation of a building site.'
              )
            },
            {
              value: 'Principal Contractor',
              text: t('signup:applicationType.principalContractor', 'Principal contractor'),
              iconSrc: '/static/images/PrincipalContractor.svg',
              tooltip: t(
                'signup:applicationType.principalContractorDetails',
                'The contractor with control over the construction phase of a project to plan, manage,  monitor and coordinate health and safety involving more than one contractor.'
              )
            },
            {
              value: 'Designer',
              text: t('signup:applicationType.designer', 'Designer'),
              iconSrc: '/static/images/Designer.svg',
              tooltip: t(
                'signup:applicationType.designerDetails',
                'Individuals or organisations such as architects, consulting engineers or quantity surveyors. This includes anyone who specifies and alters designs as part of their work.'
              )
            },
            {
              value: 'Principal Designer',
              text: t('signup:applicationType.principalDesigner', 'Principal designer'),
              iconSrc: '/static/images/PrincipalDesigner.svg',
              tooltip: t(
                'signup:applicationType.principalDesignerDetails',
                'An organisation or individual who takes the lead in planning, managing, monitoring and coordinating health and safety during the pre-construction phase of a project.'
              )
            },
            {
              value: 'SSIP Approved (Non-Construction)',
              text: t('signup:applicationType.ssipApproved', 'SSIP approved (non‑construction)'),
              iconSrc: '/static/images/SSIPApproved.svg',
              tooltip: t(
                'signup:applicationType.ssipApprovedDetails',
                'Not working in connection with any construction activities. e.g. Transportation or Catering Services.'
              )
            },
            {
              value: 'Care',
              text: t('signup:applicationType.care', 'Care'),
              iconSrc: '/static/images/Care.svg',
              tooltip: t(
                'signup:applicationType.careDetails',
                'An organisation that provides services such as residential nursing homes, domiciliary/day care, or other support services in the care sector.'
              )
            }
          ],
          requiredErrorText: t('signup:validation.selectApplicationType', 'Please select an application type.')
        }
      ],
      skipInStepper: true,
      visibleIf:
        '({packageType} = GB && {organizationType} = Contractor) || ({packageType} = GB && {organizationType} = GroupContractor)',
      stepNumber: 2
    },
    {
      title: t('signup:pages.assessmentType', 'Assessment type'),
      stepName: SurveySteps.AssessmentType,
      elements: [
        {
          type: 'mui-list',
          name: 'assessmentType',
          title: t('signup:fields.chooseAssessmentType', 'Choose your Health & Safety assessment type'),
          isRequired: true,
          choices: [
            {
              value: 'Direct Application',
              text: t('signup:assessmentType.fullApplication', 'Full application'),
              iconSrc: '/static/images/FullApplication.svg',
              tooltip: t(
                'signup:applicationType.fullApplicationDetails',
                'A full assessment of your Health & Safety Management System, hosted online and reviewed via a qualified CHAS assessor.'
              )
            },
            {
              value: 'DTS',
              text: t('signup:assessmentType.deemToSatisfy', 'Deem to satisfy'),
              iconSrc: '/static/images/DeemToSatisfy.svg',
              tooltip: t(
                'signup:applicationType.deemToSatisfyDetails',
                'You can ONLY obtain a Deemed to Satisfy (DTS) Certificate if you have a valid assessment with another SSIP scheme operator. Please ensure you have completed the SSIP element with your certification body, so that your certificate details are populated on the SSIP portal. If you do not hold a valid assessment with another SSIP operator, you must apply for a CHAS Assessment.'
              ),
              visibleIf:
                '({applicationType} = Care && {organizationType} != GroupContractor) || ({applicationType} != Care && {organizationType} = GroupContractor) || {organizationType} = Contractor'
            },
            {
              value: 'Assured',
              text: t('signup:assessmentType.assuredOnSiteAudit', 'Assured (on-site audit)'),
              iconSrc: '/static/images/Assured.svg',
              tooltip: t(
                'signup:applicationType.assuredOnSiteAuditDetails',
                'A full assessment of your Health  & Safety Management System, in the form of an in-person visit from a qualified CHAS assessor.'
              ),
              visibleIf: '{applicationType} != Care'
            }
          ],
          requiredErrorText: t('signup:validation.thisIsRequiredField', 'This is a required field')
        }
      ],
      skipInStepper: true,
      visibleIf:
        '({packageType} = GB && {organizationType} = Contractor) || ({packageType} = GB && {organizationType} = GroupContractor)',
      stepNumber: 2
    },
    {
      title: t('signup:pages.companyDetails', 'Company details'),
      stepName: SurveySteps.CompanyDetails,
      elements: [
        {
          name: 'companyDetailsPanel',
          type: 'panel',
          title: t('signup:pages.companyDetails', 'Company details'),
          description: t('signup:fields.companyAndAddressDetails', 'Enter your company’s name and main address below.'),
          elements: [
            {
              type: 'mui-company-search',
              name: 'companyName',
              title: t('signup:fields.companyName', 'Company name'),
              isRequired: true,
              customDisabled: '{packageType} = AU && {company} notempty',
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.companyName', 'Company name'),
                number: 1
              }),
              validators: [
                {
                  type: 'text',
                  minLength: 1,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.companyName', 'Company name'),
                    number: 1
                  })
                },
                {
                  type: 'text',
                  maxLength: 160,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.companyName', 'Company name'),
                    number: 160
                  })
                },
                {
                  type: 'expression',
                  expression: 'checkIfCompanyNameExists({companyName})',
                  text: t('signup:validation.companyExists', 'Company already exists, please contact support')
                }
              ],
              titleLocation: 'hidden',
              clearIfInvisible: 'none',
              providers: {
                GB: {
                  provider: 'CompanyHouse'
                },
                AU: {
                  provider: 'AustralianBusinessRegistry',
                  hasDetails: true
                }
              }
            },
            {
              type: 'mui-countries',
              name: 'country',
              title: t('signup:fields.country.title', 'Country or region'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.countryIsRequired', 'Country is required'),
              enableIf: '{manualEntering} == true',
              strings: {
                searchPlaceholder: t('signup:fields.country.searchCountries', 'Search Countries'),
                countriesPlaceholder: t('signup:fields.country.selectCountry', 'Select Country')
              }
            },
            {
              type: 'mui-address',
              name: 'address1',
              enableIf:
                '({country} notempty && {manualEntering} == true) || ({company} notempty && {company.addressLine1} empty) || ({company} notempty && {packageType} = AU)',
              title: t('signup:fields.addressLine1', 'Address line 1'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.addressLine1', 'Address line 1'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 100,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    number: 100
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    number: 2
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                },
                {
                  type: 'expression',
                  text: '',
                  expression: 'showDetails()'
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'address2',
              parentClassName: 'long-input',
              visibleIf: '{showDetails}',
              enableIf:
                '{manualEntering} == true || ({company} notempty && {company.addressLine1} empty) || {packageType} = AU',
              title: t('signup:fields.addressLine2', 'Address line 2'),
              validators: [
                {
                  type: 'text',
                  maxLength: 100,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.addressLine2', 'Address line 2'),
                    number: 100
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.addressLine2', 'Address line 2'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'townOrCity',
              visibleIf: '{showDetails}',
              enableIf:
                '{manualEntering} == true || ({company} notempty && {company.city} empty) || ({company} notempty && {company.addressLine1} empty) || {packageType} = AU',
              title: t('signup:fields.city', 'Town or city'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.city', 'Town or city'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 40,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.city', 'Town or city'),
                    number: 40
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.city', 'Town or city'),
                    number: 2
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.city', 'Town or city'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'postalCode',
              visibleIf: '{showDetails}',
              enableIf:
                '{manualEntering} == true || ({company} notempty && {company.postalCode} empty) || ({packageType} = GB && {company} notempty && {company.addressLine1} empty)',
              title: t('signup:fields.postalCode', 'Postal code'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.postalCode', 'Postal code'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 20,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.postalCode', 'Postal code'),
                    number: 20
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.postalCode', 'Postal code'),
                    number: 2
                  })
                }
              ]
            }
          ]
        },
        {
          type: 'panel',
          title: t('signup:pages.employeeDetails', 'Employee details'),
          description: t(
            'signup:fields.employeeDetailsInfo',
            'The number of employees should include all directly employed office staff including permanent, temporary, part-time and labour-only subcontractors. Bona fide subcontractors should be counted separately.'
          ),
          elements: [
            {
              type: 'mui-text',
              name: 'totalEmployeeCount',
              title: t('signup:fields.numberOfEmployees', 'Number of employees'),
              isRequired: true,
              parentClassName: 'input-with-tooltip',
              tooltip: {
                GB: t(
                  'signup:tooltip.numberOfEmployees',
                  'The number of employees is the total number of direct employees including, permanent, temporary, part-time and labor only subcontractors. UK Labour Law states a labour-only subcontractor is also considered an employee. This does not include Bona Fide subcontractors. We recommend that you consider the total number of effective employees you may have/use at any one time during a 12-month rolling period.'
                ),
                AU: t(
                  'signup:tooltip.numberOfEmployeesAU',
                  'The number of employees is the total number of direct employees including, permanent, temporary, part-time and labour-only subcontractors.'
                )
              },
              requiredErrorText: t('signup:validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('signup:fields.numberOfEmployees', 'Number of employees')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 1,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            },
            {
              type: 'mui-text',
              name: 'subcontractorEmployeeCount',
              title: t('signup:fields.numBonaFideSubcontractors', 'Number of bona fide subcontractors'),
              titleForMobile: t('signup:fields.numBonaFideSubcontractorsShort', '# of bona fide subcontractors'),
              isRequired: true,
              parentClassName: 'input-with-tooltip',
              tooltip: t(
                'signup:tooltip.bonaFideSubcontractors',
                'Bona Fide are subcontractors who work with their own tools, insurance etc. And are usually paid by invoice. We recommend that you consider the total number of effective employees you may have/use at ony one time during a 12-month rolling period.'
              ),
              requiredErrorText: t('signup:validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('signup:fields.numBonaFideSubcontractors', 'Number of bona fide subcontractors')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 0,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            }
          ]
        }
      ],
      stepNumber: 3
    },
    {
      title: t('signup:pages.order', 'Order'),
      stepName: SurveySteps.Order,
      elements: [
        {
          type: 'mui-subscriptions',
          name: 'subscription',
          title: t('signup:fields.certificationTiers', 'Certification tier'),
          description: t(
            'signup:fields.certificationTiersDescription',
            'Select the level of compliance you would like to achieve.'
          ),
          isRequired: true,
          choicesByUrl: {
            url:
              '/api/v1/products?Location={packageType}&Country={country}&CustomerType={organizationType}&NumOfEmployees={totalEmployeeCount}{subscriptionOptionalParams}',
            valueName: 'id',
            titleName: 'subscriptionTier',
            attachOriginalItems: true,
            path: 'products'
          },
          productDetails: [
            {
              description: [
                { text: t('signup:productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('signup:productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Close' },
                {
                  text: t('signup:productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Close'
                },
                {
                  text: t('signup:productDetails.meetCommonAssessment', 'Meet Common Assessment requirements'),
                  icon: 'Close'
                },
                {
                  text: t(
                    'signup:productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Close'
                }
              ],
              visibleIf: "{value} = 'CHAS Standard'"
            },
            {
              description: [
                { text: t('signup:productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('signup:productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Check' },
                {
                  text: t('signup:productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Close'
                },
                {
                  text: t('signup:productDetails.meetCommonAssessment', 'Meet Common Assessment requirements'),
                  icon: 'Close'
                },
                {
                  text: t(
                    'signup:productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Close'
                }
              ],
              visibleIf: "{value} = 'CHAS Advanced'"
            },
            {
              description: [
                { text: t('signup:productDetails.meetSSIP', 'Meet SSIP requirements'), icon: 'Check' },
                { text: t('signup:productDetails.meetPAS', 'Meet PAS 91 requirements'), icon: 'Check' },
                {
                  text: t('signup:productDetails.meetPublicSector', 'Meet public sector requirements'),
                  icon: 'Check'
                },
                {
                  text: t('signup:productDetails.meetCommonAssessment', 'Meet Common Assessment requirements'),
                  icon: 'Check'
                },
                {
                  text: t(
                    'signup:productDetails.recognisedMostComprehensive',
                    'Recognised industry-wide as the most comprehensive'
                  ),
                  icon: 'Check'
                }
              ],
              isMostPopular: true,
              visibleIf: "{packageType} = GB && {value} = 'CHAS Elite'"
            },
            {
              description: [
                {
                  text: t(
                    'signup:productDetails.AUCHASEliteText',
                    'CHAS Elite is the elite standard of CHAS accreditation and includes all assessments under Verified Contractor and seven additional risk areas: health and safety (H&S), quality, environmental, equality, diversity and inclusion (EDI), anti-bribery and corruption, corporate social responsibility (CSR) and information security.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{packageType} = AU && {value} = 'CHAS Elite'"
            },
            {
              description: [
                {
                  text: t(
                    'signup:productDetails.GBCHASVerifiedSupplierText',
                    'Membership package tailored for Suppliers to validate their capabilities and showcase their certifications and qualifications.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{packageType} = GB && {value} = 'CHAS Verified Supplier'"
            },
            {
              description: [
                {
                  text: t(
                    'signup:productDetails.CHASVerifiedContractorText',
                    'Verified Contractor is our entry-level CHAS assessment package, offering contractor accreditation in three areas of risk management: identity, insurance and modern slavery. Gain accreditation, begin prequalifying for contracts and win more work.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'CHAS Verified Contractor'"
            },
            {
              description: [
                {
                  text: t(
                    'signup:productDetails.AUCHASVerifiedSupplierText',
                    'Verified Supplier is our risk management verification service specifically tailored for material suppliers. It is a streamlined and comprehensive risk management solution that allows suppliers to highlight their compliance with supply-chain best practices, regulatory controls and international standards.'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{packageType} = AU && {value} = 'CHAS Verified Supplier'"
            }
          ],
          productIcons: [
            { iconSrc: '/static/images/Standard.svg', visibleIf: "{subscriptionTier} = 'CHAS Standard'" },
            { iconSrc: '/static/images/Advanced.svg', visibleIf: "{subscriptionTier} = 'CHAS Advanced'" },
            { iconSrc: '/static/images/Elite.svg', visibleIf: "{subscriptionTier} = 'CHAS Elite'" },
            {
              iconSrc: '/static/images/VerifiedSupplier.svg',
              visibleIf: "{subscriptionTier} = 'CHAS Verified Supplier'"
            },
            {
              iconSrc: '/static/images/VerifiedContractor.svg',
              visibleIf: "{subscriptionTier} = 'CHAS Verified Contractor'"
            }
          ],

          orderByOriginalItem: 'price:asc',
          filterChoicesOnRender:
            "expandChoicesByUrl({item}, productType) != 'Services' && expandChoicesByUrl({item}, subscriptionTier) != 'CHAS Joining Fee'",
          requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.'),
          price: 'formatPrice(getPrice({value}, getPriceWithDiscount({value}, true)))',
          strings: {
            year: t('signup:subscription.year', '/year*'),
            yearNoTax: t('signup:subscription.yearNoTax', '/year'),
            mostPopular: t('signup:subscription.mostPopular', 'Most popular!'),
            select: t('signup:subscription.select', 'Select'),
            selected: t('signup:subscription.selected', 'Selected')
          }
        },
        {
          type: 'mui-checkbox',
          name: 'dataSharingConsent',
          title: t(
            'signup:dataSharingConsent.labelText',
            'I agree to relevant information held by Veriforce CHAS being shared with other Recognised Assessment Bodies for the Common Assessment Standard. I am an authorised representative with sufficient delegated powers to provide the consent specified above. {0}'
          ),
          links: [
            {
              text: t(
                'signup:dataSharingConsent.link',
                'Learn more about the Common Assessment Standard and data sharing.'
              ),
              href:
                'https://veriforce-prod-cp-cacentral1-versioned.s3.ca-central-1.amazonaws.com/chas/Key-Questions-for-Data-Sharing.pdf'
            }
          ],
          titleLocation: 'hidden',
          visibleIf: "{packageType} = GB && expandChoicesByUrl({subscription}, subscriptionTier) = 'CHAS Elite'"
        },
        {
          type: 'mui-additional-products',
          name: 'additionalProduct',
          title: t('signup:fields.supportPackages', 'Choose your support package'),
          description: t(
            'signup:fields.supportPackagesDetails',
            'Whether you require a fast tracked application or additional support and guidance, we offer a variety of different options to suit your budget and needs.'
          ),
          isRequired: true,
          choicesFromQuestion: 'subscription',
          orderByOriginalItem: 'price:asc',
          filterChoicesOnRender:
            "expandChoicesByUrl({item}, productType) = 'Services' && expandChoicesByUrl({item}, subscriptionTier) != 'CHAS Joining Fee'",
          requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.'),
          selfServeText: t('signup:additionalProducts.selfServe', 'Self Serve'),
          visibleIf: '{subscription} notempty && !!expandChoicesByUrl(Services, id, productType)',
          price: 'formatPrice(getPrice({value}, getPriceWithDiscount({value}, true)))',
          productDetails: [
            {
              description: [
                {
                  text: t(
                    'signup:additionalProducts.assessmentTurnaround',
                    '{value}-day assessment turnaround**'
                  ).replace('{value}', '2'),
                  icon: ''
                },
                {
                  text: t('signup:additionalProducts.awardWinningCustomerSupport', 'Award-winning customer support'),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'Fast Track'"
            },
            {
              description: [
                {
                  text: t(
                    'signup:additionalProducts.assessmentTurnaround',
                    '{value}-day assessment turnaround**'
                  ).replace('{value}', '2'),
                  icon: ''
                },
                {
                  text: t('signup:additionalProducts.awardWinningCustomerSupport', 'Award-winning customer support'),
                  icon: ''
                },
                {
                  text: t(
                    'signup:additionalProducts.dedicatedOneTwoOneAssistance',
                    'Dedicated 1-2-1 health and safety expert assistance'
                  ),
                  icon: ''
                },
                {
                  text: t(
                    'signup:additionalProducts.documentsTemplatesSupport',
                    'Additional support documents and templates'
                  ),
                  icon: ''
                }
              ],
              visibleIf: "{value} = 'CHAS Assist'"
            }
          ],
          strings: {
            selfServeAdditionalProducts: [
              t('signup:additionalProducts.assessmentTurnaround', '{value}-day assessment turnaround**')
                .replace('{value}', '10')
                .replace('**', ''),
              t('signup:additionalProducts.awardWinningCustomerSupport', 'Award-winning customer support')
            ],
            includesFastTrack: t('signup:additionalProducts.includesFastTrack', 'Includes Fast Track')
          }
        },
        {
          type: 'html',
          html: '<div class="sd-element no-vertical-padding"><div class="order-divider"/></div>'
        },
        {
          type: 'mui-discount',
          name: 'discountCode',
          title: t('signup:fields.discount.title', 'Reference code'),
          description: t(
            'signup:fields.discount.description',
            'Optional: If you have been supplied a reference code by a Client or Trade Association, please enter it here.'
          ),
          validatorUrl: '/api/v1/products/validateDiscountCode',
          enableIf: '{subscription} notempty',
          strings: {
            apply: t('signup:common.apply', 'Apply'),
            remove: t('signup:common.remove', 'Remove'),
            codeApplied: t('signup:fields.discount.codeApplied', 'applied'),
            codeNotFound: t('signup:fields.discount.codeNotFound', "Code doesn't exist."),
            codeExpired: t('signup:fields.discount.codeExpired', 'Code is expired.'),
            codeCannotByApplied: t(
              'signup:fields.discount.codeCannotByApplied',
              "Code doesn't apply to selected items."
            ),
            clientNotFound: t('signup:fields.discount.clientNotFound.', 'Client not found'),
            unknownError: t('signup:unknownError', 'Unknown error, please try again later.')
          }
        },
        ...(bankTransferEnabled
          ? [
              {
                type: 'mui-radiogroup',
                name: 'paymentMethod',
                title: t('signup:fields.paymentMethod', 'Payment Method'),
                isRequired: true,
                choices: [
                  {
                    value: PaymentMethod.CARD,
                    text: t('signup:paymentMethod.card', 'Credit / Debit Card'),
                    iconSrc: '/static/images/PaymentMethods.png'
                  },
                  {
                    value: PaymentMethod.BANK_TRANSFER,
                    text: t('signup:paymentMethod.bankTransfer', 'Bank Transfer'),
                    description: t(
                      'signup:paymentMethod.bankTransferDescription',
                      'You will receive a Stripe invoice for payment. Access will be restricted until payment is made in full.'
                    )
                  }
                ],
                requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.')
              }
            ]
          : []),
        {
          type: 'html',
          name: 'Cost Breakdown',
          html: ` <div class="total-price cost-breakdown">
                      <span class="pricing-explanation sd-description sd-title">
                      {subscription} - {basePackagePrice}* <br />
                        <span class="pricing-explanation sd-description sd-title">
                      Joining fee - {joiningFee}*
                  </div>
                `,
          visibleIf: '!!expandChoicesByUrl({subscription}, subscriptionTier)'
        },
        {
          type: 'html',
          name: 'Cost Breakdown',
          html: ` <div class="total-price cost-breakdown">
                      <span class="pricing-explanation sd-description sd-title">
                      {additionalProductName} - {additionalProductPrice}* <br />
                  </div>
                `,
          visibleIf: '!!{additionalProductName}'
        },
        {
          type: 'html',
          name: 'total',
          html: ` <div class="total-price">
                      <span class="sd-title">${t(
                        'signup:fields.total',
                        'Total:'
                      )} {subscriptionTotalPrice} </span> <span class="total-price-explanation-text"> {subscriptionTotalPriceExplanationText} </span>
                  </div>
                `,
          visibleIf: '!!expandChoicesByUrl({subscription}, subscriptionTier)'
        },
        {
          type: 'html',
          html: '<div class="sd-element no-vertical-padding"><div class="order-divider"/></div>',
          visibleIf: '{isAdmin} != false'
        },
        {
          type: 'mui-privacy-consent',
          name: 'privacyConsent',
          titleLocation: 'hidden',
          title: t(
            'signup:privacyConsent.title',
            'By placing this order I confirm that I have read and agree with the {0} and {1}.'
          ),
          links: [
            {
              text: t('signup:privacyConsent.tos', 'Terms of Service'),
              href: 'https://veriforce.com/terms-of-service'
            },
            {
              text: t('signup:privacyConsent.privacyPolicy', 'Privacy Policy'),
              href: 'https://veriforce.com/privacy-policy'
            }
          ],
          visibleIf: '{isAdmin} == false'
        },
        {
          type: 'html',
          html: '<div class="sd-element no-vertical-padding total-price-explanation">{totalExplanation}</div>',
          visibleIf: '{totalExplanation} notempty'
        },
        {
          type: 'html',
          html: `<div class="sd-element no-vertical-padding"><p class='fast-track-explanation'>
          ${t(
            'signup:fastTrackExplanation.part1',
            '**Initial H&S assessment completed within 2 working days of submission.'
          )} <br>
          ${t('signup:fastTrackExplanation.part2', 'DTS checks will be completed within 1 working day.')} <br>
          ${t('signup:fastTrackExplanation.part3', 'Other Elite Assessed modules may take up to 5 working days.')} <br>
          ${t('signup:fastTrackExplanation.part4', 'Fast Track is not applicable for on-site audits.')} </p></div>`,
          visibleIf: '!!showAdditionalProductExplanationText(expandChoicesByUrl({subscription}, subscriptionTier)'
        },
        {
          type: 'html',
          html: `<div class="sd-element no-vertical-padding"><p class='pricing-explanation'>${t(
            'signup:priceExplanationGeneral',
            'CHAS uses a dynamic pricing system and prices vary depending on certification level and the number of employees your business has.'
          )} </p></div>`
        },
        ...(recurringBillingEnabled
          ? [
              {
                type: 'html',
                html: `<div class="sd-element no-vertical-padding"><p class='pricing-explanation'>${t(
                  'signup:autoRenewalInfo',
                  'The membership is a continuous service billed annually. You have the right to opt out of auto-renewal by providing us with notice at least 14 days prior to the renewal.'
                )}</p></div>`
              }
            ]
          : [])
      ],
      stepNumber: 4,
      completePage: true,
      showPrevButton: false
    }
  ],
  calculatedValues: [
    {
      name: 'subscriptionOptionalParams',
      expression:
        "iif({isSubscriptionDisplayed}, '&ready=true' + iif({applicationType} notempty, '&ApplicationType=' + {applicationType}, '') + iif({assessmentType} notempty, '&AssessmentType=' + {assessmentType}, ''), '')"
    },
    {
      name: 'totalExplanation',
      expression: `
        iif(
          {taxRate.isActive} && getJoiningFee() > 0,
          '${t(
            'signup:pricesExcludeTaxAndJoiningFee',
            '*Prices exclude {tax}. New members pay a one-off joining fee of {price}.'
          )
            .replace('{tax}', "' + {taxRate.name} + '")
            .replace('{price}', "' + formatPrice(getPriceWithDiscount(getJoiningFee('id'), true)) + '")}',
          iif(
            {taxRate.isActive},
            '${t('signup:pricesExcludeTax', '* Prices exclude {tax}').replace('{tax}', "' + {taxRate.name} + '")}',
              iif({country} = AU && {taxRate.isActive} = false, '${t(
                'signup:notRegisteredForTax',
                'Not registered for {tax}.'
              ).replace('{tax}', "' + {taxRate.name} + '")}', '')
          ) + iif(getJoiningFee() > 0, '${t(
            'signup:pricesExcludeJoiningFee',
            '* New members pay a one-off joining fee of {price}'
          ).replace('{price}', "' + formatPrice(getPriceWithDiscount(getJoiningFee('id'), true)) + '")}', '')
        )
      `
    },
    {
      name: 'totalWithDiscountAmount',
      expression:
        "sum(getPriceWithDiscount({subscription}), getPriceWithDiscount({additionalProduct}), iif({subscription}, getPriceWithDiscount(getJoiningFee('id')), 0))"
    },
    {
      name: 'totalAmount',
      expression:
        'sum(expandChoicesByUrl({subscription}, price), expandChoicesByUrl({additionalProduct}, price), iif({subscription}, getJoiningFee(), 0))'
    },
    {
      name: 'basePackagePrice',
      expression: 'iif({subscription}, formatPrice(expandChoicesByUrl({subscription}, price)), -1)'
    },
    {
      name: 'additionalProductPrice',
      expression: 'iif({additionalProduct}, formatPrice(expandChoicesByUrl({additionalProduct}, price), 0)'
    },
    {
      name: 'additionalProductName',
      expression: 'showAdditionalProductName()'
    },
    {
      name: 'joiningFee',
      expression: 'iif({subscription}, formatPrice(getJoiningFee()), true)'
    },
    {
      name: 'relativeDiscountAmount',
      expression: 'formatPrice({totalAmount} - {totalWithDiscountAmount})'
    },
    {
      name: 'subscriptionTotalPrice',
      expression:
        'formatPrice(sum(getPriceTotal(getPriceWithDiscount({subscription})), getPriceTotal(getPriceWithDiscount({additionalProduct})), iif({subscription}, getPriceTotal(getPriceWithDiscount(getJoiningFee("id"))), 0)))'
    },
    {
      name: 'subscriptionTotalPriceExplanationText',
      expression: `iif({taxRate.isActive}, '${t('signup:totalTaxExplanation', '(inc {tax})').replace(
        '{tax}',
        "' + {taxRate.name} + '"
      )}', ' ')`
    },
    {
      name: 'marketingText',
      expression: `iif({packageType} = AU,
      '${t(
        'signup:fields.marketingTextAU',
        'We’re excited for you to join CHAS, the leading compliance and risk management provider for contractors. Fill out the fields below to begin creating your account.'
      )}',
      '${t(
        'signup:fields.marketingTextGB',
        'We’re excited for you to join CHAS, UK’s leading compliance and risk management provider for contractors. Fill out the fields below to begin creating your account.'
      )}')`
    }
  ],
  clearInvisibleValues: 'onHiddenContainer',
  showCompletedPage: false,
  showQuestionNumbers: 'off',
  questionErrorLocation: 'bottom',
  pagePrevText: t('signup:navigation.back', 'Back'),
  pageNextText: t('signup:navigation.next', 'Next'),
  completeText: t('signup:navigation.orderNow', 'Order Now')
});
